import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Typography,
  Space,
} from 'antd';
import { 
  UserOutlined,
  CrownOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api';

const { Title } = Typography;

const Dashboard: React.FC = () => {
  const {
    data: subscriptionSummary,
    isLoading: loadingSubscriptions
  } = useQuery({
    queryKey: ['subscription-summary'],
    queryFn: () => adminApi.getSubscriptionsSummary(),
  });

  const {
    data: dailyUsage,
    isLoading: loadingUsage
  } = useQuery({
    queryKey: ['daily-usage'],
    queryFn: () => adminApi.getDailyUsage(),
  });

  const {
    data: usersData,
    isLoading: loadingUsers
  } = useQuery({
    queryKey: ['dashboard-users'],
    queryFn: () => adminApi.getUsers({ limit: 1 }), // Just to get total count
  });

  return (
    <div className="page-content">
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Title level={2}>Dashboard</Title>
        </Col>
      </Row>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loadingUsers}>
            <Statistic
              title="Total Users"
              value={usersData?.total_count || 0}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loadingSubscriptions}>
            <Statistic
              title="Active Subscriptions"
              value={subscriptionSummary?.active_subscriptions || 0}
              prefix={<CrownOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loadingUsage}>
            <Statistic
              title="Today's Try-ons"
              value={dailyUsage?.total_try_on || 0}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card loading={loadingUsage}>
            <Statistic
              title="Today's Smart Adds"
              value={dailyUsage?.total_smart_add || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Subscription Breakdown */}
      {subscriptionSummary && (
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={12}>
            <Card title="Subscription Breakdown" loading={loadingSubscriptions}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="Free Users"
                    value={subscriptionSummary.free_users || 0}
                    valueStyle={{ color: '#8c8c8c' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Bronze Users"
                    value={subscriptionSummary.bronze_users || 0}
                    valueStyle={{ color: '#CD7F32' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Silver Users"
                    value={subscriptionSummary.silver_users || 0}
                    valueStyle={{ color: '#C0C0C0' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Gold Users"
                    value={subscriptionSummary.gold_users || 0}
                    valueStyle={{ color: '#FFD700' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card title="Today's Activity" loading={loadingUsage}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Statistic
                    title="AI Stylist Requests"
                    value={dailyUsage?.total_ai_stylist || 0}
                    valueStyle={{ color: '#eb2f96' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Active Users Today"
                    value={dailyUsage?.unique_users || 0}
                    valueStyle={{ color: '#13c2c2' }}
                  />
                </Col>
                <Col span={24}>
                  <Statistic
                    title="Total API Calls Today"
                    value={(dailyUsage?.total_try_on || 0) + (dailyUsage?.total_smart_add || 0) + (dailyUsage?.total_ai_stylist || 0)}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Quick Actions */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Quick Actions">
            <Space size="large">
              <div>
                <Title level={4} style={{ marginBottom: '8px' }}>Welcome to Closet Admin</Title>
                <p style={{ color: '#666', marginBottom: 0 }}>
                  Use the navigation menu to manage users, view subscription details, 
                  and access comprehensive reports about your application usage.
                </p>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;